import type { SubtitlesEntity } from '../../../../../../core/data-sources/content-components/types.js';

export interface OptimizerProps {
  layout?: string;
  cropFactor?: number;
  width?: number;
}

interface VideoOptimizerProps extends OptimizerProps {
  urls: string[];
}

interface Asset {
  url?: string;
  width?: number;
  height?: number;
}

interface ForcePlayWhenVisibleProps {
  video: HTMLVideoElement;
  resumePlaying: boolean;
  fallbackFunction?: () => void;
}

/**
 * Get a rough estimate of what the video player size might be. This is pretty
 * much a shoot in the dark since we have very little information to go on
 * (specially during SSR).
 *
 * Should only be used as fallback when video element itself is not able to
 * provide a valid element width
 */
const getEstimatePlayerSize = ({ layout }: { layout?: string }) => {
  const screenWidth = window?.innerWidth || 400;
  const isMobile = screenWidth <= 768;

  if (isMobile) {
    return screenWidth; // Most things under this size are practically fullWidth
  }
  switch (layout) {
    case 'fullPageWidth':
    case 'fullScreen':
      return screenWidth;
    case 'fullWidth':
      return 980;
    case 'fullWidthMobile':
    case 'extendedContent':
    case undefined:
    case 'default':
    default:
      return 980 - 300;
  }
};

const convertVideoUrlsToAssets = (urls: string[] = []): Asset[] => {
  const sizeRegex = /.*\/(\d+)_(\d+)_(\d+)\..*$/;

  return urls.map((url) => {
    const matches = url.match(sizeRegex);

    return {
      width: matches ? Number(matches[1]) : undefined,
      height: matches ? Number(matches[2]) : undefined,
      url,
    };
  });
};

const getOptimalAsset = (rawAssets: Asset[] = [], targetWidth: number) => {
  // Sort from low to high, remove unusable assets
  const assets = rawAssets
    .sort((a, b) => (a.width && b.width ? a.width - b.width : 0))
    .filter(({ url }) => url);

  let optimalAsset = assets.find(
    (size) => size.width && size.width >= targetWidth,
  );

  /**
   * Use largest available asset if we had a target size, but didn't find
   * anything big enough
   */
  if (!optimalAsset && targetWidth !== 0) {
    optimalAsset = assets.pop();
  }

  // If not fallback to smallest
  if (!optimalAsset) {
    optimalAsset = assets.shift();
  }

  return optimalAsset;
};

const getOptimalSize = ({
  layout,
  cropFactor = 1,
  width = 0,
}: OptimizerProps) => {
  let optimalSize = width * cropFactor;

  // Fallback to guessing size based on indicating factors
  if (!optimalSize) {
    optimalSize =
      getEstimatePlayerSize({
        layout,
      }) * cropFactor;
  }

  return optimalSize;
};

export const getOptimalVideoVariant = ({
  urls,
  ...optimizerProps
}: VideoOptimizerProps) => {
  const videoAssets = convertVideoUrlsToAssets(urls);

  return getOptimalAsset(videoAssets, getOptimalSize(optimizerProps))?.url;
};

export const forcePlayWhenVisible = ({
  video,
  resumePlaying,
  fallbackFunction,
}: ForcePlayWhenVisibleProps) => {
  if (video) {
    if (video.paused && resumePlaying) {
      let result;
      try {
        result = video.play().catch(() => {
          if (fallbackFunction) {
            fallbackFunction();
          }
        });
      } catch (_error) {
        if (fallbackFunction) {
          fallbackFunction();
        }
      }
      if (result === undefined) {
        if (fallbackFunction) {
          fallbackFunction();
        }
      }
    }
    if (!video.paused && !resumePlaying) {
      video.pause();
    }
  }
};

export const addVisibleClassnameWhenLoaded = (video: HTMLVideoElement) => {
  if (video) {
    if (video.readyState >= video.HAVE_FUTURE_DATA) {
      video.classList.add('visible');
    } else {
      video.addEventListener(
        'canplay',
        () => {
          video.classList.add('visible');
        },
        false,
      );
    }
  }
};

export const unique = (array: string[]) => {
  return [...new Set(array)];
};

export const getSubtitles = (subtitles: SubtitlesEntity[], language: string) =>
  subtitles?.find((s) => s.language === language);
